import { PlusIcon } from '@heroicons/react/16/solid';
import { useState } from 'react';
import { toast } from 'sonner';

import { useDialogState } from '../../../hooks/useDialogState';
import {
  useCreateEncounterMutation,
  useDeleteEncounterMutation,
  useGetEncountersQuery,
  useUpdateEncounterMutation,
} from '../../../services/api/treatmentService';
import { EncounterSchema } from '../../../services/types/treatment-types';
import { Button } from '../../ui/catalyst/button';
import { DeleteEncounterDialog } from './DeleteEncounterDialog';
import { EncounterDetailView } from './EncounterDetailView';
import { EncounterDialog } from './EncounterDialog';
import { EncountersMacroView } from './EncountersMacroView';

interface EncountersOverviewProps {
  matterId: string;
  providerId: string;
}

export const EncountersOverview = ({
  matterId,
  providerId,
}: EncountersOverviewProps) => {
  // Fetch encounters directly from API to ensure we have mentions
  const { data: encounters = [], isLoading } = useGetEncountersQuery(
    { matterId, providerId },
    { skip: false },
  );

  const [createEncounter] = useCreateEncounterMutation();
  const [updateEncounter] = useUpdateEncounterMutation();
  const [deleteEncounter] = useDeleteEncounterMutation();

  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(
    null,
  );
  const selectedEncounter =
    encounters.find((e) => e.id === selectedEncounterId) || null;

  const addDialog = useDialogState();
  const editDialog = useDialogState();
  const deleteDialog = useDialogState();

  const [currentEncounter, setCurrentEncounter] =
    useState<EncounterSchema | null>(null);
  const [formState, setFormState] = useState({
    date: '',
    description: '',
    services: [] as string[],
  });

  const openAddDialog = () => {
    setFormState({
      date: '',
      description: '',
      services: [],
    });
    addDialog.openDialog();
  };

  const openEditDialog = (encounter: EncounterSchema) => {
    setCurrentEncounter(encounter);
    setFormState({
      date: encounter.date || '',
      description: encounter.description || '',
      services: encounter.services || [],
    });
    editDialog.openDialog();
  };

  const openDeleteDialog = (encounter: EncounterSchema) => {
    setCurrentEncounter(encounter);
    deleteDialog.openDialog();
  };

  const handleAddEncounter = async () => {
    try {
      await createEncounter({
        matterId,
        providerId,
        encounter: formState,
      }).unwrap();
      toast.success('Encounter successfully added');
      addDialog.closeDialog();
    } catch (error) {
      console.error('Failed to add encounter:', error);
      toast.error('Failed to add encounter');
    }
  };

  const handleUpdateEncounter = async () => {
    if (currentEncounter?.id) {
      try {
        await updateEncounter({
          matterId,
          providerId,
          encounterId: currentEncounter.id,
          encounter: formState,
        }).unwrap();
        toast.success('Encounter successfully updated');
        editDialog.closeDialog();
      } catch (error) {
        console.error('Failed to update encounter:', error);
        toast.error('Failed to update encounter');
      }
    }
  };

  const handleDeleteEncounter = async () => {
    if (currentEncounter?.id) {
      try {
        await deleteEncounter({
          matterId,
          providerId,
          encounterId: currentEncounter.id,
        }).unwrap();
        toast.success('Encounter successfully deleted');
        deleteDialog.closeDialog();
        // If we're deleting the currently selected encounter, go back to the list view
        if (selectedEncounterId === currentEncounter.id) {
          setSelectedEncounterId(null);
        }
      } catch (error) {
        console.error('Failed to delete encounter:', error);
        toast.error('Failed to delete encounter');
      }
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="overflow-hidden bg-white sm:rounded-lg sm:shadow-sm">
        <div className="border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
          <div className="-mt-1 -ml-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
            <div className="mt-1 ml-4">
              <h3 className="text-base font-semibold text-gray-900">
                Treatment History
              </h3>
            </div>
          </div>
        </div>
        <div className="p-6 text-center text-sm text-gray-500">
          Loading encounters...
        </div>
      </div>
    );
  }

  const handleSelectEncounter = (encounterId: string) => {
    setSelectedEncounterId(encounterId);
  };

  const handleBackToList = () => {
    setSelectedEncounterId(null);
  };

  return (
    <div className="overflow-hidden bg-white sm:rounded-lg sm:shadow-sm">
      <div className="border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
        <div className="-mt-1 -ml-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
          <div className="mt-1 ml-4">
            <h3 className="text-base font-semibold text-gray-900">
              Treatment History
            </h3>
          </div>
          <div className="mt-1 ml-4 shrink-0">
            <Button plain type="button" onClick={openAddDialog}>
              <PlusIcon className="h-3.5 w-3.5 mr-1" />
              Add
            </Button>
          </div>
        </div>
      </div>

      <div className="overflow-auto">
        {selectedEncounterId ? (
          <EncounterDetailView
            encounter={selectedEncounter as EncounterSchema}
            onBack={handleBackToList}
            onEdit={() =>
              selectedEncounter && openEditDialog(selectedEncounter)
            }
            onDelete={() =>
              selectedEncounter && openDeleteDialog(selectedEncounter)
            }
          />
        ) : (
          <EncountersMacroView
            encounters={encounters}
            onSelectEncounter={handleSelectEncounter}
            onAddEncounter={openAddDialog}
          />
        )}
      </div>

      {/* Add Encounter Dialog */}
      <EncounterDialog
        isOpen={addDialog.isOpen}
        onClose={addDialog.closeDialog}
        title="Add New Encounter"
        description="Record a new treatment encounter for this medical provider."
        initialData={formState}
        onSubmit={handleAddEncounter}
        submitButtonText="Add Encounter"
        onChange={setFormState}
      />

      {/* Edit Encounter Dialog */}
      <EncounterDialog
        isOpen={editDialog.isOpen}
        onClose={editDialog.closeDialog}
        title="Edit Encounter"
        description="Update the details of this treatment encounter."
        currentEncounter={currentEncounter || undefined}
        initialData={formState}
        onSubmit={handleUpdateEncounter}
        submitButtonText="Update Encounter"
        formPrefix="edit_"
        onChange={setFormState}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteEncounterDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.closeDialog}
        onDelete={handleDeleteEncounter}
      />
    </div>
  );
};
